/*
 * kw-colour-card-image.js  --  the 12 colour collection pages only.
 *
 * Two rules, both asked for explicitly:
 *   1. a card must show the colour the page is about;
 *   2. if that colour is sold out, the product is removed from the page.
 *
 * WHY: a card renders variant 1, so a case that merely *comes in* pink shows its
 * black photo on /collections/pinke-handyhuellen. Measured across the catalogue,
 * 51% of cards on the colour pages showed the wrong colour. And where the pink
 * variant was sold out, picking "the pink one" would have shown a customer a
 * colour they cannot buy -- hence rule 2.
 *
 * HOW: every Boost card already carries a data-product JSON blob holding
 *   images[]              -> {src, position, ...}
 *   options_with_values[] -> {name:"farbe", position:1, values:[{title, image}]}
 *   variants              -> a JSON *string* of {title, id, available}
 * where `image` is the 1-BASED position of that colour's photo, and a variant
 * title is its option values joined by " / " in option order. So the right
 * picture and the stock state are already in the DOM; nothing is fetched.
 *
 * No URL change and no Boost filter is applied, so the page keeps its clean
 * /collections/<handle> URL and shows no filter chips.
 *
 * KNOWN LIMIT: hiding happens in the browser, so Boost's "N Produkte" count and
 * its pagination still count the hidden products. Fixing that needs the
 * collection membership itself to change server-side.
 *
 * Built 21 Sep 2026. Theme-resident -- see THEME_LAUNCH_CHECKLIST.md; it is lost
 * on a publish of any theme not descended from this one.
 */
(function () {
  'use strict';

  /* family -> the Farbe option values that belong to it. Option values are
     German on both storefronts (the UK store translates the label, not the
     value), so one table serves kwmobile.com and kwmobile.co.uk alike. */
  var FAMILIES = {
    "blaue": ["Azurblau", "Baltik Blau", "Blau", "Blau matt", "Blue Reef", "Cool Glacier", "Deep Ocean", "Dunkelblau", "Dunkelblau matt", "Heidelbeerblau", "Hellblau", "Hellblau matt", "Hochglanz Blau", "Iris Blau", "Jeansblau", "Karibikblau", "Kornblumenblau", "Marineblau", "Meerblau", "Metallic Blau", "Metallic Dunkelblau", "Metallic Karibikblau", "Metallic Petrol", "Metallic Zodiac Blue", "Neon Blau", "Pastellblau", "Petrol", "Petrol matt", "Sea Breeze", "Strahlend Blau", "Taubenblau", "Velvet Blue", "Vintage Blue"],
    "braune": ["Beige", "Beige matt", "Braun", "Bronze", "Cappuccino", "Chocolate", "Cognac", "Dunkelbraun", "Hellbraun", "Helltaupe", "Macchiato", "Metallic Bronze", "Mokka", "Natur", "Sand", "Taupe", "Vintage Braun"],
    "gelbe": ["Gelb", "Gelb matt", "Hellgelb", "Honiggelb", "Marigold", "Neon Gelb", "Pastellgelb", "Pastellgelb matt", "Safran", "Strahlend Gelb", "Zitronengelb"],
    "graue": ["Anthrazit", "Antique Stone", "Arctic Night", "Blaugrau", "Bräunlichgrau", "Dunkelgrau", "Dunkler Schiefer", "Grau", "Hellgrau", "Hellgrau matt", "Hochglanz Silber", "Metallic Grau", "Metallic Silber", "Silber", "Stone Dust"],
    "gruene": ["Blaugrün", "Cool Mint", "Dunkelgrün", "Dunkelgrün matt", "Dunkelolivgrün", "Grasgrün", "Graugrün", "Grün", "Grüne Tomate", "Grüner Pfeffer", "Hellgrün", "Limettengrün", "Matcha Grün", "Metallic Dunkelgrün", "Metallic Mintgrün", "Metallic Tannengrün", "Metallic Türkis", "Mintgrün", "Mintgrün matt", "Moosgrün", "Neon Grün", "Neon Türkis", "Olivgrün", "Olivgrün matt", "Pastellgrün", "Pfefferminzgrün", "Pistaziengrün", "Pixie Green", "Porzellan Grün", "Rainwashed Grün", "Smaragdgrün", "Tannengrün", "Türkis", "Türkis matt", "Türkisgrün", "sanftes Moosgrün"],
    "lila": ["Blauviolett", "Brombeere", "Flieder", "Grape", "Helle Iris", "Hyacinth Purple", "Lavendel", "Lavendelblau", "Lavendelgrau", "Lavendelgrau matt", "Magenta Lila", "Mauve", "Metallic Arctic Berry", "Metallic Blauviolett", "Metallic Brombeere", "Metallic Lavendel", "Metallic Violett", "Mulberry", "Neon Violett", "Nude Lilac", "Orchidee Lila", "Orchidee Violett", "Pastell Lavendel", "Pastelllila", "Pflaume", "Purple Cloud", "Soft Blue Lavender", "Veilchen Lila", "Violett", "rosa lavendel"],
    "orange": ["Apricot", "Fruity Orange", "Mandarin Orange", "Mango", "Metallic Orange", "Neon Orange", "Orange", "Orange Clay", "Orange matt", "Rusty Orange", "Summer Orange", "Warme Aprikose"],
    "pinke": ["Altrosa", "Altrosa matt", "Awesome Pink", "Dark Rose", "Fuchsia", "Fuchsia matt", "Grapefruit Pink", "Hellrosa", "Hellrosa matt", "Himbeer Pink", "Hochglanz Rosegold", "Karminrosa", "Koralle", "Koralle matt", "Leuchtende Koralle", "Magenta", "Metallic Pink", "Metallic Rosegold", "Neon Koralle", "Neon Koralle matt", "Neon Pink", "Pastell Koralle", "Pastell Pfirsich", "Pfirsich", "Pink", "Pink matt", "Rosa", "Rosa matt", "Rosegold", "Rosegold matt", "Soft Blush", "Vintage Pink", "Winter Rose"],
    "rote": ["Bordeaux", "Bordeaux Violett", "Dunkelrot", "Granatapfelrot", "Hochglanz Rot", "Klassisch Rot", "Metallic Dunkelrot", "Metallic Tawny Red", "Neon Rot", "Rhabarber Rot", "Rot", "Rot matt", "Spice Red", "Sweet Cherry", "Tawny Red", "Tomatenrot"],
    "schwarze": ["Hochglanz Schwarz", "Metallic Schwarz", "Schwarz", "Schwarz matt"],
    "transparente": ["Matt Transparent", "Transparent"],
    "weisse": ["Coconut Swirl", "Creme", "Creme matt", "Moon Glow", "Perlmutt", "Weiß", "Weiß matt", "Zuckerguss"],
    "herbst": ["Bordeaux Violett", "Rhabarber Rot", "Tawny Red", "Granatapfelrot", "Sweet Cherry", "Dark Rose", "Winter Rose", "Orange Clay", "Warme Aprikose", "Pastell Pfirsich", "Honiggelb", "Tannengrün", "Moosgrün", "Dunkelgrün matt", "Smaragdgrün", "Graugrün", "Petrol matt", "Macchiato", "Cappuccino", "Chocolate", "Taupe", "Bräunlichgrau", "Coconut Swirl", "Creme", "Antique Stone", "Dunkler Schiefer"]
  };

  var HANDLES = {
    "blaue-handyhuellen": "blaue",
    "blue-phone-cases": "blaue",
    "braune-handyhuellen": "braune",
    "brown-phone-cases": "braune",
    "gelbe-handyhuellen": "gelbe",
    "yellow-phone-cases": "gelbe",
    "graue-handyhuellen": "graue",
    "grey-phone-cases": "graue",
    "gruene-handyhuellen": "gruene",
    "green-phone-cases": "gruene",
    "lila-handyhuellen": "lila",
    "purple-phone-cases": "lila",
    "orange-handyhuellen": "orange",
    "orange-phone-cases": "orange",
    "pinke-handyhuellen": "pinke",
    "pink-phone-cases": "pinke",
    "rote-handyhuellen": "rote",
    "red-phone-cases": "rote",
    "schwarze-handyhuellen": "schwarze",
    "black-phone-cases": "schwarze",
    "transparente-handyhuellen": "transparente",
    "clear-phone-cases": "transparente",
    "weisse-handyhuellen": "weisse",
    "white-phone-cases": "weisse",
    "herbst-aktion": "herbst",
    "autumn-sale": "herbst"
  };

  var handle = (location.pathname.match(/\/collections\/([^\/?#]+)/) || [])[1];
  var family = handle && FAMILIES[HANDLES[handle.toLowerCase()]];
  if (!family) return;

  var WANTED = Object.create(null);
  for (var i = 0; i < family.length; i++) WANTED[family[i]] = true;

  var DONE = 'kwColourImg';
  var WIDTHS = [180, 360, 540, 720];

  function sized(src, w) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function farbeOption(data) {
    var opts = data.options_with_values || [];
    for (var i = 0; i < opts.length; i++) {
      var n = (opts[i].original_name || opts[i].name || '').toLowerCase();
      if (n === 'farbe' || n === 'colour' || n === 'color') return opts[i];
    }
    return null;
  }

  /* data.variants is a JSON string, not an array */
  function variants(data) {
    if (Array.isArray(data.variants)) return data.variants;
    try { return JSON.parse(String(data.variants)) || []; } catch (e) { return []; }
  }

  /* the colour segment of a variant title, by the Farbe option's position */
  function colourOf(title, pos) {
    var parts = String(title == null ? '' : title).split(' / ');
    return (parts[(pos || 1) - 1] || parts[0] || '').trim();
  }

  /* first family colour on this product that is actually buyable */
  function pick(data) {
    var opt = farbeOption(data);
    if (!opt || !opt.values || !data.images || !data.images.length) return null;

    var stocked = Object.create(null);
    var vs = variants(data);
    for (var v = 0; v < vs.length; v++) {
      if (vs[v] && vs[v].available) stocked[colourOf(vs[v].title, opt.position)] = true;
    }
    /* a product with no parsable variant list must not be silently deleted */
    var known = vs.length > 0;

    for (var i = 0; i < opt.values.length; i++) {
      var val = opt.values[i];
      if (!WANTED[val.title]) continue;
      if (known && !stocked[val.title]) continue;      /* rule 2 */
      var pos = val.image;
      if (!pos) continue;
      for (var k = 0; k < data.images.length; k++) {
        if (data.images[k] && data.images[k].position === pos) return data.images[k].src;
      }
      var img = data.images[pos - 1];
      if (img) return img.src;
    }
    return null;
  }

  function card(el) {
    return el.closest('.boost-sd__product-item, .boost-sd__product-list-item') || el;
  }

  function apply(el) {
    if (el.dataset[DONE]) return;
    var raw = el.getAttribute('data-product');
    if (!raw) return;
    var data;
    try { data = JSON.parse(raw); } catch (e) { return; }

    el.dataset[DONE] = '1';
    var src = pick(data);

    if (!src) {                                        /* rule 2: remove it */
      var host = card(el);
      host.style.display = 'none';
      host.setAttribute('data-kw-colour-hidden', '1');
      return;
    }

    var img = el.querySelector('img');
    if (!img || !img.src || img.src.indexOf(src.split('?')[0]) !== -1) return;

    /* srcset must go too, or the browser keeps choosing the old candidate */
    img.removeAttribute('data-srcset');
    img.srcset = WIDTHS.map(function (w) { return sized(src, w) + ' ' + w + 'w'; }).join(', ');
    img.src = sized(src, 540);
  }

  function sweep() {
    var els = document.querySelectorAll('[data-product]');
    for (var i = 0; i < els.length; i++) apply(els[i]);
  }

  /* Boost hydrates late and re-renders on sort, paging and filtering, so a single
     pass on DOMContentLoaded catches nothing. Observe instead, debounced. */
  var timer = null;
  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; sweep(); }, 60);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();


/* Boost card sale price: one card, one variant. 24 Sep 2026.
 *
 * WHY: a Boost card is product-level, but promotions here are variant-level.
 * Boost renders priceMin (the cheapest of ALL variants) against
 * compareAtPriceMin (which only DISCOUNTED variants carry), so a product whose
 * cheapest colour is not reduced advertises a discount nobody can buy.
 * Galaxy S21 case + glass: black photo, "13,99 statt 19,99, -30%", where
 * 13,99 is Altrosa (not on sale), 19,99 is Bordeaux's was-price and Bordeaux
 * actually sells at 15,99 (-20%).
 *
 * FIX, on every page with Boost cards (not just the promo collection):
 * only cards that render a struck price are touched; for each one the real
 * variant prices are read from /products/<handle>.js and the card is rewritten
 * to describe ONE variant -- photo, link, price, was-price. Which variant:
 *   - on a colour page (kw-colour-card-image.js above): the colour that page
 *     already put on the card;
 *   - anywhere else: the first in-stock colour that is actually reduced.
 * If no in-stock variant is reduced, the strike and badges are removed.
 * No rate is hard-coded: the next campaign needs no change here. The corner
 * badge (kw-sale-badge.js) computes its % from the prices this leaves behind.
 */
(function () {
  'use strict';

  var FAMILY_OF = {
    "blaue-handyhuellen": "blaue", "blue-phone-cases": "blaue",
    "braune-handyhuellen": "braune", "brown-phone-cases": "braune",
    "gelbe-handyhuellen": "gelbe", "yellow-phone-cases": "gelbe",
    "graue-handyhuellen": "graue", "grey-phone-cases": "graue",
    "gruene-handyhuellen": "gruene", "green-phone-cases": "gruene",
    "lila-handyhuellen": "lila", "purple-phone-cases": "lila",
    "orange-handyhuellen": "orange", "orange-phone-cases": "orange",
    "pinke-handyhuellen": "pinke", "pink-phone-cases": "pinke",
    "rote-handyhuellen": "rote", "red-phone-cases": "rote",
    "schwarze-handyhuellen": "schwarze", "black-phone-cases": "schwarze",
    "transparente-handyhuellen": "transparente", "clear-phone-cases": "transparente",
    "weisse-handyhuellen": "weisse", "white-phone-cases": "weisse"
  };
  var handle = ((location.pathname.match(/\/collections\/([^\/?#]+)/) || [])[1] || '').toLowerCase();
  /* colour pages already swap the photo; here we only need to know the page
     pins a colour, and read which one from the photo kw-colour-card-image set */
  var colourPage = !!FAMILY_OF[handle];

  var DONE = 'kwSaleCard';
  var WIDTHS = [180, 360, 540, 720];
  var cache = {};
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function json(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; });
  }
  /* Boost carries the German handle; on kwmobile.co.uk that 404s as .js, so
     follow the storefront's own redirect to the English handle and retry. */
  function product(h) {
    if (!cache[h]) {
      cache[h] = json(root + 'products/' + h + '.js')
        .then(function (p) {
          if (p) return p;
          return fetch(root + 'products/' + h, { method: 'HEAD', credentials: 'same-origin' })
            .then(function (r) {
              var path = new URL(r.url).pathname.replace(/\/$/, '');
              return r.ok && path.indexOf('/products/') !== -1 ? json(path + '.js') : null;
            });
        })
        .catch(function () { return null; });
    }
    return cache[h];
  }

  function farbeOption(d) {
    var o = d.options_with_values || [];
    for (var i = 0; i < o.length; i++) {
      var n = (o[i].original_name || o[i].name || '').toLowerCase();
      if (n === 'farbe' || n === 'colour' || n === 'color') return o[i];
    }
    return null;
  }
  /* /products/<h>.js names colours in the storefront language ("Bordeaux
     Violet" on .co.uk) while the card's own data speaks German, so colours are
     matched through the card's variant list by id, never by name. */
  var TITLES = 'kwTitles';
  function titles(d) {
    if (d[TITLES]) return d[TITLES];
    var vs = d.variants, map = {};
    if (!Array.isArray(vs)) { try { vs = JSON.parse(String(vs)) || []; } catch (e) { vs = []; } }
    for (var i = 0; i < vs.length; i++) if (vs[i]) map[String(vs[i].id)] = vs[i].title;
    return (d[TITLES] = map);
  }
  function colourOf(v, pos, d) {
    var parts = String(titles(d)[String(v.id)] || '').split(' / ');
    return (parts[(pos || 1) - 1] || parts[0] || '').trim();
  }
  function imageFor(d, opt, colour) {
    if (!opt || !d.images) return null;
    for (var i = 0; i < opt.values.length; i++) {
      if (opt.values[i].title !== colour) continue;
      var pos = opt.values[i].image;
      if (!pos) return null;
      for (var k = 0; k < d.images.length; k++) {
        if (d.images[k] && d.images[k].position === pos) return d.images[k].src;
      }
      return d.images[pos - 1] ? d.images[pos - 1].src : null;
    }
    return null;
  }
  function reduced(v) { return v.compare_at_price && v.compare_at_price > v.price; }

  /* the colour whose photo the card is currently showing (colour pages) */
  function shownColour(d, opt, card) {
    var img = card.querySelector('img');
    var src = img && (img.currentSrc || img.src) || '';
    for (var i = 0; opt && i < opt.values.length; i++) {
      var s = imageFor(d, opt, opt.values[i].title);
      if (s && src.indexOf(s.split('?')[0].replace(/^https?:/, '')) !== -1) return opt.values[i].title;
    }
    return null;
  }

  function target(d, opt, p, card) {
    var vs = p.variants || [], pos = opt ? opt.position : 1, i;
    if (colourPage) {
      var c = shownColour(d, opt, card);
      if (!c) return null;
      var any = null;
      for (i = 0; i < vs.length; i++) {
        if (colourOf(vs[i], pos, d) !== c) continue;
        if (vs[i].available) return vs[i];
        any = any || vs[i];
      }
      return any;
    }
    /* option order, as the swatches show it */
    var order = opt ? opt.values.map(function (x) { return x.title; }) : [];
    var best = null, bestRank = 1e9;
    for (i = 0; i < vs.length; i++) {
      if (!vs[i].available || !reduced(vs[i])) continue;
      var r = order.indexOf(colourOf(vs[i], pos, d));
      if (r === -1) r = 1e6 + i;
      if (r < bestRank) { best = vs[i]; bestRank = r; }
    }
    return best;
  }

  /* rewrite the number inside "€13,99" / "£7.43", keeping symbol and separator */
  function formatLike(txt, cents) {
    var sep = /\d,\d{2}(?!\d)/.test(txt) ? ',' : '.';
    return String(txt).replace(/[0-9]+(?:[.,][0-9]{2})?/, (cents / 100).toFixed(2).replace('.', sep));
  }

  function setImage(card, src) {
    var img = card.querySelector('img');
    if (!img || !src || (img.src || '').indexOf(src.split('?')[0]) !== -1) return;
    var base = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=';
    img.removeAttribute('data-srcset');
    img.srcset = WIDTHS.map(function (w) { return base + w + ' ' + w + 'w'; }).join(', ');
    img.src = base + 540;
  }

  function setLinks(card, id) {
    var links = card.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var href = (links[i].getAttribute('href') || '').split('#')[0].replace(/([?&])variant=\d+&?/, '$1').replace(/[?&]$/, '');
      links[i].setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + 'variant=' + id);
    }
  }

  function render(card, v) {
    var saleWrap = card.querySelector('.boost-sd__product-price--sale');
    var sale = saleWrap && (saleWrap.querySelector('.boost-sd__format-currency') || saleWrap);
    var cmp = card.querySelector('.boost-sd__product-price--compare');
    var was = cmp && (cmp.querySelector('.boost-sd__format-currency') || cmp);
    var sr = card.querySelector('.boost-sd__product-price .boost-sd__visually-hidden');
    var label = card.querySelector('.boost-sd__product-label--sale');
    if (!sale) return;

    var nowTxt = formatLike(sale.textContent, v.price);
    if (sale.textContent !== nowTxt) sale.textContent = nowTxt;

    /* the screen-reader line is rebuilt from its first render, so it can be
       rewritten in either direction when the customer switches colour */
    if (sr && sr.dataset.kwTpl == null) sr.dataset.kwTpl = sr.textContent;

    if (reduced(v)) {
      var wasTxt = formatLike(was ? was.textContent : sale.textContent, v.compare_at_price);
      if (was && was.textContent !== wasTxt) was.textContent = wasTxt;
      if (cmp) cmp.style.display = '';
      if (label) label.style.display = '';
      if (sr) sr.textContent = sr.dataset.kwTpl
        .replace(/[0-9]+[.,][0-9]{2}/, wasTxt.replace(/[^0-9.,]/g, ''))
        .replace(/([0-9]+[.,][0-9]{2})(?![\s\S]*[0-9]+[.,][0-9]{2})/, nowTxt.replace(/[^0-9.,]/g, ''));
    } else {
      /* not reduced: no strike, no Sale label. Hidden rather than removed so a
         later swatch click can bring them back; the was-price is set equal to
         the price so kw-sale-badge.js finds no reduction and hides its %. */
      if (was && was.textContent !== nowTxt) was.textContent = nowTxt;
      if (cmp) cmp.style.display = 'none';
      if (label) label.style.display = 'none';
      if (sr) sr.textContent = nowTxt;
    }
  }

  function fix(el) {
    if (el.dataset[DONE]) return;
    var card = el.closest('.boost-sd__product-item, .boost-sd__product-list-item');
    if (!card || !card.querySelector('.boost-sd__product-price--compare')) return;
    var d;
    try { d = JSON.parse(el.getAttribute('data-product')); } catch (e) { return; }
    if (!d || !d.handle) return;
    el.dataset[DONE] = '1';

    product(d.handle).then(function (p) {
      if (!p || !p.variants) return;
      var opt = farbeOption(d);
      var v = target(d, opt, p, card);
      if (!v) {
        /* nothing reduced is in stock: show the cheapest buyable price, no strike */
        var cheapest = null;
        for (var i = 0; i < p.variants.length; i++) {
          var x = p.variants[i];
          if (x.available && (!cheapest || x.price < cheapest.price)) cheapest = x;
        }
        if (cheapest) render(card, { price: cheapest.price, compare_at_price: null });
        return;
      }
      if (!colourPage && opt) setImage(card, imageFor(d, opt, colourOf(v, opt.position, d)));
      setLinks(card, v.id);
      render(card, v);
      card[STATE] = { d: d, opt: opt, p: p };
    });
  }

  /* Colour dots on the card, 29 Sep 2026. Boost swaps the photo when a dot is
     clicked but leaves price and link alone, so a card showing the black case
     still said 8,23 statt 10,29 (the Petrol price) and opened Petrol. Point
     link and price at the clicked colour instead. Matched on the card's
     German name or the storefront's own (English on .co.uk). */
  var STATE = '__kwSaleCard';
  function variantFor(s, colour) {
    var pos = s.opt ? s.opt.position : 1, vs = s.p.variants, any = null;
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i];
      if (colourOf(v, pos, s.d) !== colour && v['option' + pos] !== colour) continue;
      if (v.available) return v;
      any = any || v;
    }
    return any;
  }
  /* Boost cancels the native radio toggle, so no change event fires: react to
     the click (or Enter/Space) on the dot's label instead. */
  function pickColour(e) {
    var lab = e.target && e.target.closest && e.target.closest('label.boost-sd__radio-label');
    var card = lab && lab.closest('.boost-sd__product-item, .boost-sd__product-list-item');
    var s = card && card[STATE];
    if (!s) return;
    var input = lab.htmlFor && document.getElementById(lab.htmlFor);
    var colour = input ? input.value : String(lab.getAttribute('aria-label') || '').replace(/^[^:]*:/, '');
    var v = variantFor(s, String(colour || '').trim());
    if (!v) return;
    setLinks(card, v.id);
    render(card, v);
  }
  document.addEventListener('click', pickColour, true);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') pickColour(e);
  }, true);

  /* Boost's own click handler navigates to its stored product URL, dropping
     the ?variant= set above, so a click on a reduced card opened colour #1 at
     full price. On cards this script manages, keep the click away from Boost
     and let the browser follow the link (ctrl/middle-click still work). */
  window.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a || a.getAttribute('href').indexOf('variant=') === -1) return;
    var card = a.closest('.boost-sd__product-item, .boost-sd__product-list-item');
    if (card && card[STATE]) e.stopPropagation();
  }, true);

  function sweep() {
    var els = document.querySelectorAll('[data-product]');
    for (var i = 0; i < els.length; i++) fix(els[i]);
  }
  var timer = null;
  function schedule() { if (!timer) timer = setTimeout(function () { timer = null; sweep(); }, 80); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
})();
